import { useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  LOCALES,
  LOCALE_LABELS,
  OFFER_KINDS,
  formatPrice,
  groszToZloty,
  offerInputSchema,
  zlotyToGrosz,
  type Locale,
  type Offer,
  type OfferInput,
  type OfferKind,
  type OfferPatch,
} from '@diana/shared'
import { api, type ContactSettings } from '../../lib/api.js'
import { Column, Main, TopTitle } from '../../classroom/Shell.js'
import { Button, ErrorLine, RowText, Skeleton, Status, Tabs } from '../../classroom/ui.js'
import {
  Check,
  CoverLine,
  Field,
  Grip,
  More,
  asLocale,
  byOrder,
  errorMessage,
  orderChanges,
  slugify,
  useDragOrder,
  useInvalidate,
  useListCache,
  useReorder,
} from './editor.js'

/**
 * The pricing editor (Studio-Offers.png).
 *
 * This screen is the reason the whole system exists in this shape: Diana
 * raises a price in ten seconds, with no developer, no deploy and no
 * conversation. That makes changing prices an experiment she can run and
 * reverse — see docs/00-ROADMAP.md §2.
 *
 * Two panes: the positions of one language in the order the site shows them
 * (drag the ⋮⋮ grip to change it), and the selected position's form. The
 * language and the selection live in the URL (?jezyk=uk&pozycja=…), so a
 * reload keeps her place.
 */
const NEW = 'nowa'

export function OffersPage() {
  const [params, setParams] = useSearchParams()
  const locale = asLocale(params.get('jezyk'))
  const wanted = params.get('pozycja')
  const { data, isPending, isError } = useQuery({ queryKey: ['studio', 'offers'], queryFn: api.studio.offers })
  /** She picked a row herself — on a phone the form, below the list, scrolls into view. */
  const [scroll, setScroll] = useState(false)

  const go: Go = (next, { replace = false, pick = false } = {}) => {
    const p = new URLSearchParams()
    if (next.locale !== 'pl') p.set('jezyk', next.locale)
    if (next.id) p.set('pozycja', next.id)
    setScroll(pick)
    setParams(p, { replace })
  }

  const offers = data ? byOrder(data.filter((o) => o.locale === locale)) : []
  const first = offers[0]?.id

  /*
   * With nothing chosen the form shows the first position. Write that into
   * the URL, so the choice stays put when a drag brings another row to the top.
   */
  useEffect(() => {
    if (!wanted && first) go({ locale, id: first }, { replace: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wanted, first, locale])

  if (isPending || isError || !data) {
    return (
      <Column density="studio" top={<TopTitle studio>Cennik</TopTitle>}>
        <Main density="studio">{isPending ? <Skeleton lines={6} /> : <ErrorLine>Nie udało się wczytać cennika. Odśwież stronę.</ErrorLine>}</Main>
      </Column>
    )
  }

  const isNew = wanted === NEW
  const original = isNew ? undefined : (offers.find((o) => o.id === wanted) ?? offers[0])
  const key = isNew ? `new:${locale}` : (original?.id ?? `empty:${locale}`)

  return <PriceList key={key} locale={locale} offers={offers} original={original} isNew={isNew} picked={scroll} go={go} />
}

type Go = (next: { locale: Locale; id?: string }, how?: { replace?: boolean; pick?: boolean }) => void

/* ── the screen: list + form ───────────────────────────────────────────── */

/** What each kind does for the student — the option text says it, so no hint is needed. */
const KIND_OPTION: Record<OfferKind, string> = {
  trial: 'Rozmowa poznawcza — rezerwuje termin od razu',
  single: 'Pojedyncza lekcja — uczeń prosi o termin',
  package: 'Pakiet lekcji — uczeń prosi o stały termin',
  contact: 'Kontakt — bez rezerwacji, przycisk prowadzi pod link',
}

const plural = (n: number, one: string, few: string, many: string) =>
  n === 1 ? one : n % 10 >= 2 && n % 10 <= 4 && !(n % 100 >= 12 && n % 100 <= 14) ? few : many

/** „60 min · 80 zł za 2 osoby” — the line under a position's name. */
function meta(o: Offer): string {
  return [
    o.kind === 'contact' ? 'Kontakt' : o.kind === 'package' ? `${o.lessons} ${plural(o.lessons, 'lekcja', 'lekcje', 'lekcji')}` : null,
    o.durationMin > 0 ? `${o.durationMin} min` : null,
    o.priceLabel || (o.priceGrosz === 0 ? 'bezpłatnie' : `${formatPrice(o.priceGrosz)}${o.priceNote ? ` ${o.priceNote}` : ''}`),
  ]
    .filter(Boolean)
    .join(' · ')
}

function PriceList({
  locale,
  offers,
  original,
  isNew,
  picked,
  go,
}: {
  locale: Locale
  /** This language's positions, in site order. */
  offers: Offer[]
  /** The position in the form; undefined for a new one or an empty language. */
  original?: Offer
  isNew: boolean
  /** She chose this position from the list — on a phone, bring the form into view. */
  picked: boolean
  go: Go
}) {
  const invalidate = useInvalidate('offers')
  const cache = useListCache<Offer>('offers')
  const editing = Boolean(original) || isNew
  const baseline = original ? draftFromOffer(original) : emptyDraft(locale, offers.length)
  const [d, setD] = useState<Draft>(baseline)
  const [note, setNote] = useState<{ ok: boolean; text: string } | null>(null)
  const paneRef = useRef<HTMLElement>(null)

  const set = <K extends keyof Draft>(key: K, value: Draft[K]) => {
    setNote(null)
    setD((prev) => ({ ...prev, [key]: value }))
  }
  const dirty = editing && JSON.stringify(d) !== JSON.stringify(baseline)

  useEffect(() => {
    if (picked && window.matchMedia('(max-width: 820px)').matches) paneRef.current?.scrollIntoView({ block: 'start' })
  }, [picked])

  /** Leave this position — after asking, when something is not saved. */
  function leave(next: { locale: Locale; id?: string }, pick = false) {
    if (dirty && !window.confirm('Masz niezapisane zmiany w tej pozycji. Porzucić je?')) return
    go(next, { pick })
  }

  const save = useMutation({
    mutationFn: async (input: OfferInput) => {
      if (!original) return api.studio.createOffer(input)
      const patch = diffOffer(original, input)
      if (Object.keys(patch).length === 0) return original
      return api.studio.updateOffer(original.id, patch)
    },
    onSuccess: (row) => {
      cache.put(row)
      invalidate()
      // A new position, or one moved to another language: follow it there.
      if (!original || row.locale !== locale) go({ locale: row.locale, id: row.id })
      else {
        setD(draftFromOffer(row))
        setNote({ ok: true, text: 'Zapisane — już widać na stronie.' })
      }
    },
    onError: (err) => setNote({ ok: false, text: errorMessage(err) }),
  })

  const visibility = useMutation({
    mutationFn: (active: boolean) => api.studio.updateOffer(original!.id, { active }),
    onSuccess: (row) => {
      cache.put(row)
      invalidate()
      setD((prev) => ({ ...prev, active: row.active }))
      setNote({ ok: true, text: row.active ? 'Pozycja znowu jest na stronie.' : 'Pozycja ukryta — strona jej nie pokazuje.' })
    },
    onError: () => setNote({ ok: false, text: 'Nie udało się zmienić widoczności. Spróbuj jeszcze raz.' }),
  })

  const remove = useMutation({
    mutationFn: () => api.studio.deleteOffer(original!.id),
    onSuccess: () => {
      cache.drop(original!.id)
      invalidate()
      go({ locale })
    },
    onError: () => setNote({ ok: false, text: 'Nie udało się usunąć. Spróbuj jeszcze raz.' }),
  })

  const reorder = useReorder('offers', (id, order) => api.studio.updateOffer(id, { order }))
  const drag = useDragOrder(
    offers.map((o) => o.id),
    (ids) => reorder.mutate(orderChanges(ids, offers)),
    reorder.isPending,
  )

  function submit(e: React.FormEvent) {
    e.preventDefault()
    const result = draftToInput(d)
    if ('error' in result) {
      setNote({ ok: false, text: result.error })
      return
    }
    setNote(null)
    save.mutate(result.input)
  }

  const id = (name: string) => `offer-${name}`
  const isContact = d.kind === 'contact'

  return (
    <Column
      density="studio"
      topOnPhone
      top={
        <>
          <TopTitle studio>Cennik</TopTitle>
          <div className="ml-auto">
            <Tabs
              label="Język strony"
              value={locale}
              onChange={(l) => leave({ locale: l })}
              items={LOCALES.map((l) => ({ value: l, label: LOCALE_LABELS[l] }))}
            />
          </div>
        </>
      }
      bottom={
        <>
          {note ? (
            <span role="status" className={`min-w-0 flex-1 text-class-gloss font-semibold ${note.ok ? 'text-class-correct' : 'text-class-wrong'}`}>
              {note.ok ? '✓' : '✗'} {note.text}
            </span>
          ) : (
            <span className="min-w-0 flex-1 text-class-gloss text-class-ink-soft">Zmiany są widoczne na stronie od razu.</span>
          )}
          {editing && (
            <div className="flex shrink-0 items-center gap-5">
              {original ? (
                <button
                  type="button"
                  disabled={visibility.isPending}
                  onClick={() => visibility.mutate(!original.active)}
                  className="cl-link-quiet text-class-gloss"
                >
                  {original.active ? 'Ukryj pozycję' : 'Pokaż na stronie'}
                </button>
              ) : (
                <button type="button" onClick={() => leave({ locale })} className="cl-link-quiet text-class-gloss">
                  Anuluj
                </button>
              )}
              <Button type="submit" form="offer-form" disabled={!dirty || save.isPending} className="min-h-11 text-class-gloss">
                {save.isPending ? 'Zapisuję…' : original ? 'Zapisz' : 'Dodaj pozycję'}
              </Button>
            </div>
          )}
        </>
      }
    >
      <main className="mx-auto flex max-w-[1040px] flex-col gap-10 px-5 pt-8 pb-10 desk:flex-row desk:items-start desk:gap-12 desk:px-12 desk:pt-10 desk:pb-12">
        {/* ── the list ── */}
        <section className="flex flex-col gap-3.5 desk:w-[320px] desk:shrink-0">
          <h1 className="cl-heading m-0">Pozycje w cenniku</h1>
          <p className="m-0 text-class-gloss text-class-ink-soft">
            {offers.length === 0
              ? 'Dopóki nie dodasz tu pozycji, strona w tym języku pokazuje polski cennik.'
              : 'Kolejność jak na stronie. Przeciągnij, żeby zmienić.'}
          </p>
          {offers.length > 0 && (
            <ol className="m-0 mt-2 list-none p-0">
              {drag.order.map((oid) => {
                const o = offers.find((x) => x.id === oid)!
                const current = !isNew && o.id === original?.id
                const tag = !o.active ? 'Ukryta' : o.featured ? 'Wyróżniona' : null
                return (
                  <li key={o.id} ref={drag.row(o.id)} className="border-t border-class-line first:border-t-0">
                    <div
                      className={[
                        'flex items-center gap-1.5 rounded-class-btn pl-1.5 pr-3.5',
                        current || drag.dragging === o.id ? 'bg-class-field-soft' : 'hover:bg-class-field-soft/50',
                      ].join(' ')}
                    >
                      <Grip {...drag.grip(o.id, `Przesuń „${o.title}”`)} />
                      <button
                        type="button"
                        aria-current={current ? 'true' : undefined}
                        onClick={() => !current && leave({ locale, id: o.id }, true)}
                        className="flex min-h-16 min-w-0 flex-1 cursor-pointer items-center gap-3 border-0 bg-transparent py-2.5 pr-0 pl-1 text-left font-class-text text-class-body text-class-ink"
                      >
                        <RowText title={o.title} meta={meta(o)} className={o.active ? '' : 'text-class-ink-faint'} />
                        {tag && <span className="shrink-0 text-class-label font-semibold text-class-ink-faint">{tag}</span>}
                      </button>
                    </div>
                  </li>
                )
              })}
            </ol>
          )}
          {reorder.isError && <ErrorLine>Nie udało się zapisać kolejności. Spróbuj jeszcze raz.</ErrorLine>}
          <button
            type="button"
            disabled={isNew}
            onClick={() => leave({ locale, id: NEW }, true)}
            className="cl-link mt-1 self-start pl-3.5 text-class-gloss disabled:cursor-default disabled:text-class-ink-faint"
          >
            + Dodaj pozycję
          </button>

          <ContactChats />
        </section>

        {/* ── the form ── */}
        {editing && (
          <section ref={paneRef} className="flex min-w-0 flex-1 scroll-mt-4 flex-col gap-7 border-t border-class-line pt-8 desk:border-t-0 desk:border-l desk:pt-0 desk:pl-12">
            <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
              <h2 className="cl-heading m-0">{original ? original.title : 'Nowa pozycja'}</h2>
              {original ? (
                <Status tone={original.active ? 'ok' : 'neutral'}>{original.active ? 'Widoczna na stronie' : 'Ukryta'}</Status>
              ) : (
                <span className="text-class-label font-semibold text-class-ink-faint">{LOCALE_LABELS[locale]}</span>
              )}
            </div>

            <form id="offer-form" onSubmit={submit} className="grid grid-cols-1 gap-x-6 gap-y-5 desk:grid-cols-2">
              <Field id={id('kind')} label="Rodzaj" className="desk:col-span-2">
                <select id={id('kind')} value={d.kind} onChange={(e) => set('kind', e.target.value as OfferKind)} className="cl-input">
                  {OFFER_KINDS.map((k) => (
                    <option key={k} value={k}>
                      {KIND_OPTION[k]}
                    </option>
                  ))}
                </select>
              </Field>
              <Field id={id('title')} label="Nazwa">
                <input
                  id={id('title')}
                  required
                  maxLength={120}
                  value={d.title}
                  onChange={(e) => {
                    const title = e.target.value
                    setNote(null)
                    setD((prev) => ({ ...prev, title, slug: prev.slugTouched ? prev.slug : slugify(title) }))
                  }}
                  className="cl-input"
                />
              </Field>
              <Field id={id('subtitle')} label="Podtytuł">
                <input
                  id={id('subtitle')}
                  maxLength={200}
                  value={d.subtitle}
                  onChange={(e) => set('subtitle', e.target.value)}
                  placeholder="np. 1:1, tylko Ty i ja"
                  className="cl-input"
                />
              </Field>

              <Field id={id('description')} label="Opis" className="desk:col-span-2">
                <textarea
                  id={id('description')}
                  rows={3}
                  value={d.description}
                  onChange={(e) => set('description', e.target.value)}
                  className="cl-input resize-y"
                />
              </Field>

              <Field id={id('includes')} label="Co zawiera · jedna rzecz w linii" className="desk:col-span-2">
                <textarea
                  id={id('includes')}
                  rows={Math.max(3, d.includes.split('\n').length)}
                  value={d.includes}
                  onChange={(e) => set('includes', e.target.value)}
                  placeholder={'Materiały przygotowane pod Ciebie\nZadania domowe z korektą'}
                  className="cl-input resize-y"
                />
              </Field>

              <div className="desk:col-span-2">
                <div className={`grid gap-x-4 gap-y-5 desk:gap-x-6 ${d.kind === 'package' ? 'grid-cols-2 desk:grid-cols-4' : 'grid-cols-3'}`}>
                  {d.kind === 'package' && (
                    <Field id={id('lessons')} label="Liczba lekcji">
                      <input id={id('lessons')} inputMode="numeric" value={d.lessons} onChange={(e) => set('lessons', e.target.value)} className="cl-input" />
                    </Field>
                  )}
                  <Field id={id('duration')} label="Czas (min)">
                    <input id={id('duration')} inputMode="numeric" value={d.durationMin} onChange={(e) => set('durationMin', e.target.value)} className="cl-input" />
                  </Field>
                  <Field id={id('zloty')} label="Cena (zł)">
                    <input id={id('zloty')} inputMode="decimal" value={d.zloty} onChange={(e) => set('zloty', e.target.value)} className="cl-input" />
                  </Field>
                  <Field id={id('priceNote')} label="Dopisek do ceny">
                    <input
                      id={id('priceNote')}
                      maxLength={120}
                      value={d.priceNote}
                      onChange={(e) => set('priceNote', e.target.value)}
                      placeholder="np. za 2 osoby"
                      className="cl-input"
                    />
                  </Field>
                </div>
                <p className="m-0 mt-1.5 text-class-label text-class-ink-faint">
                  Cena 0 = „Gratis”; w pakiecie — cena za cały pakiet. Czas 0 = karta nie pokazuje czasu.
                </p>
              </div>

              <Field id={id('ctaLabel')} label="Tekst przycisku" hint="Puste = domyślny tekst dla tego rodzaju.">
                <input
                  id={id('ctaLabel')}
                  maxLength={60}
                  value={d.ctaLabel}
                  onChange={(e) => set('ctaLabel', e.target.value)}
                  placeholder={isContact ? 'np. Sprawdź dostępność' : 'np. Umów lekcję'}
                  className="cl-input"
                />
              </Field>
              {isContact && (
                <Field id={id('ctaUrl')} label="Link przycisku" hint="Puste = Telegram z ustawień obok, a gdy go nie ma — strona kontaktu.">
                  <input
                    id={id('ctaUrl')}
                    maxLength={500}
                    value={d.ctaUrl}
                    onChange={(e) => set('ctaUrl', e.target.value)}
                    placeholder="https://t.me/… albo /kontakt"
                    className="cl-input"
                  />
                </Field>
              )}
              <div className={['flex flex-col gap-2.5', isContact ? 'desk:col-span-2 desk:flex-row desk:gap-8' : 'desk:pt-7'].join(' ')}>
                <Check checked={d.featured} onChange={(v) => set('featured', v)}>
                  Wyróżniona na stronie
                </Check>
                <Check checked={d.active} onChange={(v) => set('active', v)}>
                  Widoczna
                </Check>
              </div>

              <div className="desk:col-span-2">
                <CoverLine
                  coverUrl={d.coverUrl}
                  alt={d.title}
                  onChange={(media) => {
                    setNote(null)
                    setD((prev) => ({ ...prev, coverImageId: media?.id ?? null, coverUrl: media?.url ?? null }))
                  }}
                />
              </div>

              <More defaultOpen={Boolean(d.priceLabel)} summary="Więcej ustawień: tekst zamiast ceny, adres, język">
                <Field id={id('priceLabel')} label="Tekst zamiast ceny" hint="Gdy wypełniony, karta nie pokazuje kwoty.">
                  <input
                    id={id('priceLabel')}
                    maxLength={80}
                    value={d.priceLabel}
                    onChange={(e) => set('priceLabel', e.target.value)}
                    placeholder="np. Dowiedz się o dostępności"
                    className="cl-input"
                  />
                </Field>
                <Field id={id('slug')} label="Adres (slug)" hint={isContact ? undefined : 'Trafia do linku rezerwacji: /rezerwacja?oferta=…'}>
                  <input
                    id={id('slug')}
                    maxLength={120}
                    value={d.slug}
                    onChange={(e) => {
                      setNote(null)
                      setD((prev) => ({ ...prev, slug: e.target.value, slugTouched: true }))
                    }}
                    className="cl-input"
                  />
                </Field>
                {original && (
                  <Field id={id('locale')} label="Język strony" hint="Zmiana przenosi pozycję do cennika w tym języku.">
                    <select id={id('locale')} value={d.locale} onChange={(e) => set('locale', e.target.value as Locale)} className="cl-input">
                      {LOCALES.map((l) => (
                        <option key={l} value={l}>
                          {LOCALE_LABELS[l]}
                        </option>
                      ))}
                    </select>
                  </Field>
                )}
              </More>

              {original && (
                <p className="m-0 desk:col-span-2">
                  <button
                    type="button"
                    disabled={remove.isPending}
                    onClick={() => {
                      if (window.confirm(`Usunąć „${original.title}”? Tego nie da się cofnąć.`)) remove.mutate()
                    }}
                    className="cl-link-quiet text-class-gloss !text-class-wrong hover:!text-class-ink"
                  >
                    Usuń pozycję z cennika
                  </button>
                </p>
              )}
            </form>
          </section>
        )}
      </main>
    </Column>
  )
}

/* ── the form's data ───────────────────────────────────────────────────── */

/** Every field as text, the way an input holds it. Converted on save. */
type Draft = {
  locale: Locale
  slug: string
  /** Once Diana has typed a slug herself, the title stops overwriting it. */
  slugTouched: boolean
  kind: OfferKind
  lessons: string
  title: string
  subtitle: string
  description: string
  /** One bullet per line. */
  includes: string
  zloty: string
  priceNote: string
  priceLabel: string
  durationMin: string
  ctaLabel: string
  ctaUrl: string
  coverImageId: string | null
  coverUrl: string | null
  active: boolean
  featured: boolean
  order: number
}

function emptyDraft(locale: Locale, order: number): Draft {
  return {
    locale,
    slug: '',
    slugTouched: false,
    kind: 'single',
    lessons: '1',
    title: '',
    subtitle: '',
    description: '',
    includes: '',
    zloty: '0',
    priceNote: '',
    priceLabel: '',
    durationMin: '60',
    ctaLabel: '',
    ctaUrl: '',
    coverImageId: null,
    coverUrl: null,
    active: true,
    featured: false,
    order,
  }
}

function draftFromOffer(o: Offer): Draft {
  return {
    locale: o.locale,
    slug: o.slug,
    slugTouched: true,
    kind: o.kind,
    lessons: String(o.lessons),
    title: o.title,
    subtitle: o.subtitle,
    description: o.description,
    includes: o.includes.join('\n'),
    // Editing in złoty, storing in grosze. Diana should never see "12000".
    zloty: String(groszToZloty(o.priceGrosz)),
    priceNote: o.priceNote,
    priceLabel: o.priceLabel,
    durationMin: String(o.durationMin),
    ctaLabel: o.ctaLabel,
    ctaUrl: o.ctaUrl,
    coverImageId: o.coverImageId,
    coverUrl: o.coverUrl,
    active: o.active,
    featured: o.featured,
    order: o.order,
  }
}

const FIELD_NAMES: Record<string, string> = {
  locale: 'Język',
  slug: 'Adres (slug)',
  kind: 'Rodzaj',
  lessons: 'Liczba lekcji',
  title: 'Nazwa',
  subtitle: 'Podtytuł',
  description: 'Opis',
  includes: 'Co zawiera',
  durationMin: 'Czas trwania',
  priceGrosz: 'Cena',
  priceNote: 'Dopisek do ceny',
  priceLabel: 'Tekst zamiast ceny',
  ctaLabel: 'Tekst przycisku',
  ctaUrl: 'Link przycisku',
  coverImageId: 'Zdjęcie',
}

/** Turn the form into what the API accepts, or say what is wrong in Diana's words. */
function draftToInput(d: Draft): { input: OfferInput } | { error: string } {
  // An emptied field is not 0: Number('') is 0, and a price cleared to be
  // retyped was saved as „bezpłatnie” on the public Cennik.
  const zloty = d.zloty.trim() === '' ? NaN : Number(d.zloty.replace(',', '.'))
  if (!Number.isFinite(zloty) || zloty < 0) return { error: 'Cena musi być liczbą, np. 60 albo 62,50 (0, jeśli bezpłatnie).' }

  const duration = d.durationMin.trim() === '' ? 0 : Number(d.durationMin)
  if (!Number.isInteger(duration) || duration < 0) return { error: 'Czas trwania to liczba minut (0 = bez czasu).' }

  const lessons = d.kind === 'package' ? Number(d.lessons) : 1
  if (!Number.isInteger(lessons) || lessons < 1) return { error: 'Pakiet musi mieć co najmniej 1 lekcję.' }

  const candidate = {
    locale: d.locale,
    slug: d.slug.trim() || slugify(d.title),
    kind: d.kind,
    lessons,
    title: d.title.trim(),
    subtitle: d.subtitle.trim(),
    description: d.description.trim(),
    includes: d.includes
      .split('\n')
      .map((s) => s.trim())
      .filter(Boolean),
    durationMin: duration,
    priceGrosz: zlotyToGrosz(zloty),
    priceNote: d.priceNote.trim(),
    priceLabel: d.priceLabel.trim(),
    ctaLabel: d.ctaLabel.trim(),
    ctaUrl: d.kind === 'contact' ? d.ctaUrl.trim() : '',
    coverImageId: d.coverImageId,
    order: d.order,
    active: d.active,
    featured: d.featured,
  }

  const parsed = offerInputSchema.safeParse(candidate)
  if (!parsed.success) {
    const issue = parsed.error.issues[0]
    const field = FIELD_NAMES[String(issue?.path[0] ?? '')] ?? 'Pole'
    return { error: `${field}: ${issue?.message ?? 'nieprawidłowa wartość'}` }
  }
  return { input: parsed.data }
}

/** Only the keys that changed — a patch must never resend what it did not touch. */
function diffOffer(original: Offer, input: OfferInput): OfferPatch {
  const patch: Record<string, unknown> = {}
  for (const key of Object.keys(input) as (keyof OfferInput)[]) {
    const before = original[key]
    const after = input[key]
    const same = Array.isArray(after) ? JSON.stringify(after) === JSON.stringify(before) : after === before
    if (!same) patch[key] = after
  }
  return patch as OfferPatch
}

/* ── czaty ─────────────────────────────────────────────────────────────── */

/**
 * Telegram i WhatsApp — dwa czaty, w których uczeń dokańcza rozmowę.
 *
 * Jedno zapisywanie na oba pola: to jedno ustawienie w bazie, a Diana i tak
 * ustawia je raz i zapomina. The screen's one gold button is „Zapisz” for
 * the position, so this one is the white secondary.
 */
function ContactChats() {
  const queryClient = useQueryClient()
  const { data: rows } = useQuery({ queryKey: ['studio', 'settings'], queryFn: api.studio.settings })
  const contact = (rows?.find((r) => r.key === 'contact' && r.locale === null)?.value ?? {}) as ContactSettings

  const [edited, setEdited] = useState<{ telegram: string; whatsapp: string } | null>(null)
  const saved = { telegram: contact.telegram ?? '', whatsapp: contact.whatsapp ?? '' }
  const value = edited ?? saved

  const telegramValid = value.telegram.trim() === '' || /^https:\/\/\S+$/.test(value.telegram.trim())
  // Numer albo gotowy link — cokolwiek da się zamienić na czat (lib/contact.ts).
  const whatsappValid =
    value.whatsapp.trim() === '' || /^https:\/\/\S+$/.test(value.whatsapp.trim()) || value.whatsapp.replace(/\D/g, '').length >= 8
  const valid = telegramValid && whatsappValid
  const dirty = value.telegram.trim() !== saved.telegram || value.whatsapp.trim() !== saved.whatsapp

  const save = useMutation({
    // Merge, don't replace — e-mail, phone and the response-time line live in the same setting.
    mutationFn: () => api.studio.setSetting('contact', { ...contact, telegram: value.telegram.trim(), whatsapp: value.whatsapp.trim() }),
    onSuccess: () => {
      setEdited(null)
      queryClient.invalidateQueries({ queryKey: ['studio', 'settings'] })
      queryClient.invalidateQueries({ queryKey: ['content'] })
    },
  })

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        if (valid) save.mutate()
      }}
      className="mt-10 flex flex-col gap-4 border-t border-class-line pt-8"
    >
      <div>
        <h2 className="cl-subheading m-0">Telegram i WhatsApp</h2>
        <p className="m-0 mt-1.5 text-class-gloss text-class-ink-faint">
          Po rezerwacji uczeń trafia na Telegram, żebyście ustalili szczegóły. Oba linki są też w okienku rezerwacji i pod pozycjami
          „Kontakt” bez własnego linku. Puste = bez przycisku.
        </p>
      </div>
      <Field id="telegram-url" label="Link do Telegrama">
        <input
          id="telegram-url"
          inputMode="url"
          value={value.telegram}
          onChange={(e) => setEdited({ ...value, telegram: e.target.value })}
          placeholder="https://t.me/twoja_nazwa"
          aria-invalid={!telegramValid}
          className="cl-input"
        />
      </Field>
      <Field id="whatsapp-number" label="WhatsApp — numer albo link">
        <input
          id="whatsapp-number"
          inputMode="tel"
          value={value.whatsapp}
          onChange={(e) => setEdited({ ...value, whatsapp: e.target.value })}
          placeholder="+48 600 000 000"
          aria-invalid={!whatsappValid}
          className="cl-input"
        />
      </Field>
      {!telegramValid && <ErrorLine>Link do Telegrama musi zaczynać się od https://</ErrorLine>}
      {!whatsappValid && <ErrorLine>Podaj numer z kierunkowym (np. +48 600 000 000) albo pełny link https://wa.me/…</ErrorLine>}
      {save.isError && <ErrorLine>Nie udało się zapisać. Spróbuj jeszcze raz.</ErrorLine>}
      {save.isSuccess && !edited && <p className="m-0 text-class-gloss font-semibold text-class-correct">✓ Linki zapisane.</p>}
      <Button type="submit" variant="secondary" disabled={!dirty || !valid || save.isPending} className="min-h-11 self-start text-class-gloss">
        {save.isPending ? 'Zapisuję…' : 'Zapisz linki'}
      </Button>
    </form>
  )
}
